import { useMemo, useState, type FormEvent } from 'react';
import {
  Building2,
  CheckCircle2,
  ChevronLeft,
  FileCheck2,
  Flame,
  Gauge,
  Loader2,
  Mail,
  Pencil,
  Phone,
  ReceiptEuro,
  Send,
  Settings2,
  ShieldCheck,
  Sparkles,
  User,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { useComparateurStore } from './useComparateurStore';
import { StepEyebrow } from './StepEyebrow';
import { SECTOR_PROFILES, getUsageLabels } from '@shared/constants/naf-usages';
import type { ComparateurDossierPublic } from '@shared/schemas/comparateur';
import { cn } from '../../../lib/cn';
import {
  trackSubmitStart,
  trackSubmitSuccess,
  trackSubmitFailure,
  type FunnelContext,
} from '../../../lib/tracking';

const ENERGY_LABELS = {
  electricity: 'Électricité',
  gas: 'Gaz naturel',
  both: 'Électricité + Gaz',
} as const;

const COMPARATEUR_CTX: FunnelContext = {
  funnelId: 'comparateur',
  intent: 'comparer',
  source: 'comparateur',
};

interface OptimizationLever {
  id: string;
  icon: LucideIcon;
  label: string;
  hint: string;
}

/**
 * Leviers d'économie complémentaires du pôle courtage, au-delà du changement
 * de fournisseur. Transmis au consultant dans le message du dossier.
 */
const OPTIMIZATION_LEVERS: readonly OptimizationLever[] = [
  {
    id: 'turpe',
    icon: Gauge,
    label: "Réduction du coût d'acheminement",
    hint: "Votre tarif de réseau (TURPE) et votre puissance souscrite peuvent être mieux calibrés — des économies possibles sans changer de fournisseur.",
  },
  {
    id: 'cspe',
    icon: ReceiptEuro,
    label: 'Récupération partielle de la taxe électricité',
    hint: "Selon votre secteur d'activité (industrie, agriculture, transport…), une partie de la taxe sur l'électricité peut être remboursée sur justificatifs.",
  },
];

export function StepReview() {
  const store = useComparateurStore();
  const d = store.dossier;

  const [consentRgpd, setConsentRgpd] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [message, setMessage] = useState('');
  const [showRgpdError, setShowRgpdError] = useState(false);

  // Leviers suggérés par l'analyse des factures (badge « détecté »)
  const autoLevers = useMemo(
    () =>
      new Set<string>([
        ...(d?.billInsights?.summer?.suggestedLevers ?? []),
        ...(d?.billInsights?.winter?.suggestedLevers ?? []),
      ]),
    [d],
  );

  // Pré-cochage automatique depuis les insights des factures
  const [levers, setLevers] = useState<Set<string>>(() => new Set(autoLevers));

  if (!d) return null;

  function toggleLever(id: string) {
    setLevers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!consentRgpd) {
      setShowRgpdError(true);
      return;
    }
    setShowRgpdError(false);
    trackSubmitStart(COMPARATEUR_CTX);

    const leverLabels = OPTIMIZATION_LEVERS.filter((l) => levers.has(l.id)).map((l) => l.label);
    const leverNote =
      leverLabels.length > 0 ? `[Leviers demandés : ${leverLabels.join(' · ')}]` : '';
    const fullMessage = [leverNote, message.trim()].filter(Boolean).join('\n').slice(0, 2000);

    try {
      const leadId = await store.submit({
        consentRgpd: true,
        consentMarketing,
        message: fullMessage || undefined,
      });
      if (leadId) {
        trackSubmitSuccess(COMPARATEUR_CTX, leadId, {
          email: d?.contact?.email ?? undefined,
          source: 'comparateur',
        });
      }
    } catch {
      trackSubmitFailure(COMPARATEUR_CTX, 'api');
      /* message d'erreur affiché via store.error */
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => store.goBack()}
        className="-ml-1 mb-5 inline-flex min-h-11 items-center gap-1 rounded-md px-2 py-2 text-[12px] text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-700"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        Retour
      </button>

      <StepEyebrow step="review" />
      <h3 className="mt-2 h-section">
        Votre comparatif est prêt — on valide ?
      </h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-stone-500">
        Vérifiez le récapitulatif et envoyez. Un consultant vous revient sous 24 h ouvrées avec les offres comparées. Aucun changement de fournisseur sans votre accord explicite.
      </p>

      {store.error && (
        <Alert variant="error" className="mt-4">
          <AlertDescription className="text-[12px]">{store.error}</AlertDescription>
        </Alert>
      )}

      <div className="mt-6 space-y-3">
        {d.company && (
          <SummaryRow
            icon={<Building2 className="h-4 w-4 text-brand-700" />}
            label="Entreprise"
            primary={d.company.name}
            secondary={[
              `SIRET ${d.company.siret}`,
              d.company.nafCode ? `NAF ${d.company.nafCode}` : null,
              d.company.city ? `${d.company.postalCode ?? ''} ${d.company.city}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
            onEdit={() => store.setStep('lookup')}
          />
        )}

        {d.contact && (
          <SummaryRow
            icon={<User className="h-4 w-4 text-brand-700" />}
            label="Contact"
            primary={`${d.contact.firstName ?? ''} ${d.contact.lastName ?? ''}`.trim()}
            secondaryNode={
              <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-stone-500">
                {d.contact.email && (
                  <span className="inline-flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {d.contact.email}
                  </span>
                )}
                {d.contact.phoneE164 && (
                  <span className="inline-flex items-center gap-1 tabular-nums">
                    <Phone className="h-3 w-3" />
                    {d.contact.phoneE164}
                  </span>
                )}
              </span>
            }
            onEdit={() => store.setStep('contact')}
          />
        )}

        {d.energyChoice && (
          <SummaryRow
            icon={
              d.energyChoice === 'gas' ? (
                <Flame className="h-4 w-4 text-lavender-700" />
              ) : (
                <Zap className="h-4 w-4 text-accent-700" />
              )
            }
            label="Énergie comparée"
            primary={ENERGY_LABELS[d.energyChoice]}
            onEdit={() => store.setStep('energy')}
          />
        )}

        {d.usages && (d.usages.usageIds.length > 0 || d.usages.notes) && (
          <UsagesSummaryRow
            usages={d.usages}
            onEdit={() => store.setStep('usages')}
          />
        )}

        <SummaryRow
          icon={<FileCheck2 className="h-4 w-4 text-brand-700" />}
          label="Factures transmises"
          primary={
            d.bills.summer && d.bills.winter
              ? 'Été + Hiver'
              : d.bills.summer
                ? 'Été uniquement'
                : d.bills.winter
                  ? 'Hiver uniquement'
                  : d.bills.requested
                    ? 'Rappel par e-mail demandé'
                    : 'Aucune (nous vous recontacterons)'
          }
          secondary={
            d.bills.summer || d.bills.winter
              ? [d.bills.summer?.filename, d.bills.winter?.filename].filter(Boolean).join(' · ')
              : null
          }
          onEdit={() => store.setStep('bills')}
        />
      </div>

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <fieldset className="border-0 p-0 m-0">
          <legend className="text-[13px] font-semibold text-stone-800">
            Aller plus loin que le changement de fournisseur{' '}
            <span className="font-normal text-stone-400">(optionnel)</span>
          </legend>
          <p className="mt-0.5 text-[12px] leading-relaxed text-stone-500">
            Notre pôle courtage peut aussi étudier ces leviers d&apos;économie sur votre dossier.
          </p>
          <div className="mt-2.5 grid gap-2.5 sm:grid-cols-2">
            {OPTIMIZATION_LEVERS.map((lever) => {
              const isSelected = levers.has(lever.id);
              const isAutoDetected = autoLevers.has(lever.id);
              const Icon = lever.icon;
              return (
                <button
                  key={lever.id}
                  type="button"
                  onClick={() => toggleLever(lever.id)}
                  aria-pressed={isSelected}
                  className={cn(
                    'group relative flex items-start gap-3 rounded-xl border-2 p-3.5 text-left transition-all duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                    isSelected
                      ? 'border-brand-400 bg-brand-50/70 shadow-[0_4px_12px_rgba(16,185,129,0.08)]'
                      : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors',
                      isSelected ? 'bg-brand-100 text-brand-700' : 'bg-stone-100 text-stone-500',
                    )}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.7} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-1.5 text-[13px] font-semibold text-ink">
                      {lever.label}
                      {isAutoDetected && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.08em] text-accent-800">
                          <Sparkles className="h-2.5 w-2.5" />
                          Détecté sur votre facture
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 text-[11.5px] leading-snug text-stone-500">
                      {lever.hint}
                    </p>
                  </div>
                  <span
                    className={cn(
                      'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors',
                      isSelected
                        ? 'border-brand-500 bg-brand-500 text-white'
                        : 'border-stone-300 bg-white',
                    )}
                  >
                    {isSelected && <CheckCircle2 className="h-3 w-3" strokeWidth={3} />}
                  </span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <div>
          <label
            htmlFor="comparateur-message"
            className="text-[13px] font-semibold text-stone-800"
          >
            Un mot pour notre consultant <span className="font-normal text-stone-400">(optionnel)</span>
          </label>
          <textarea
            id="comparateur-message"
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={1900}
            placeholder="Date d'échéance contrat actuel, contraintes spécifiques, etc."
            className="mt-1.5 w-full rounded-xl border-2 border-stone-200 bg-white p-3 text-[13px] text-ink placeholder:text-stone-400 focus:border-brand-400 focus:outline-none"
          />
        </div>

        <ConsentCheckbox
          checked={consentRgpd}
          onChange={(v) => {
            setConsentRgpd(v);
            if (v) setShowRgpdError(false);
          }}
          error={showRgpdError ? 'Le consentement est nécessaire pour traiter votre demande.' : null}
          required
        >
          J&apos;accepte que Solution Éco France traite mes données pour étudier ma demande de
          comparaison d&apos;énergie et me recontacter. Conservation conforme à notre{' '}
          <a
            href="/legal/politique-confidentialite"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-brand-700 underline-offset-2 hover:underline"
          >
            politique de confidentialité
          </a>
          .
        </ConsentCheckbox>

        <ConsentCheckbox
          checked={consentMarketing}
          onChange={setConsentMarketing}
        >
          J&apos;accepte de recevoir, en plus de ma comparaison, des actualités sur les économies
          d&apos;énergie professionnelle (newsletter mensuelle — désabonnement en 1 clic).
        </ConsentCheckbox>

        <Button
          type="submit"
          variant="brand"
          size="xl"
          disabled={store.loading}
          className="w-full gap-2"
        >
          {store.loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Envoi en cours…
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              Envoyer ma demande
            </>
          )}
        </Button>

        <p className="flex items-center justify-center gap-1.5 text-[11px] text-stone-400">
          <ShieldCheck className="h-3 w-3 shrink-0 text-brand-500" />
          Données chiffrées, hébergées en UE. Aucun partage avec des fournisseurs sans votre accord.
        </p>
      </form>
    </div>
  );
}

function SummaryRow(props: {
  icon: React.ReactNode;
  label: string;
  primary: string;
  secondary?: string | null;
  secondaryNode?: React.ReactNode;
  onEdit?: () => void;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-stone-200 bg-white p-3.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50">
        {props.icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold uppercase tracking-[0.10em] text-stone-400">
          {props.label}
        </p>
        <p className="mt-0.5 truncate text-[13.5px] font-semibold text-ink">{props.primary}</p>
        {props.secondaryNode ? (
          <div className="mt-0.5">{props.secondaryNode}</div>
        ) : props.secondary ? (
          <p className="mt-0.5 truncate text-[12px] text-stone-500">{props.secondary}</p>
        ) : null}
      </div>
      {props.onEdit && (
        <button
          type="button"
          onClick={props.onEdit}
          className="flex min-h-11 items-center gap-1 rounded-md border border-stone-200 px-3 py-2 text-[11px] font-semibold text-stone-600 transition-colors hover:bg-stone-50 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        >
          <Pencil className="h-3 w-3" />
          Modifier
        </button>
      )}
    </div>
  );
}

function UsagesSummaryRow(props: {
  usages: NonNullable<ComparateurDossierPublic['usages']>;
  onEdit: () => void;
}) {
  const { sectorProfile, usageIds, notes } = props.usages;
  const profile = sectorProfile ? SECTOR_PROFILES[sectorProfile] : null;
  const labels = getUsageLabels(sectorProfile, usageIds);
  const primary = profile?.label ?? 'Profil personnalisé';
  return (
    <div className="flex items-start gap-3 rounded-xl border border-stone-200 bg-white p-3.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50">
        <Settings2 className="h-4 w-4 text-brand-700" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold uppercase tracking-[0.10em] text-stone-400">
          Usages déclarés
        </p>
        <p className="mt-0.5 text-[13.5px] font-semibold text-ink">{primary}</p>
        {labels.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {labels.map((u) => (
              <span
                key={u.id}
                className={cn(
                  'rounded-full px-2 py-0.5 text-[11px] font-medium',
                  u.energy === 'gas'
                    ? 'bg-lavender-50 text-lavender-700'
                    : u.energy === 'electricity'
                      ? 'bg-accent-50 text-accent-700'
                      : 'bg-brand-50 text-brand-700',
                )}
              >
                {u.label}
              </span>
            ))}
          </div>
        )}
        {notes && (
          <p className="mt-1.5 text-[12px] italic leading-relaxed text-stone-500">
            « {notes} »
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={props.onEdit}
        className="flex min-h-11 items-center gap-1 rounded-md border border-stone-200 px-3 py-2 text-[11px] font-semibold text-stone-600 transition-colors hover:bg-stone-50 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
      >
        <Pencil className="h-3 w-3" />
        Modifier
      </button>
    </div>
  );
}

function ConsentCheckbox(props: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
  error?: string | null;
  required?: boolean;
}) {
  return (
    <div>
      <label className="flex cursor-pointer items-start gap-3">
        <span
          className={cn(
            'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors',
            props.checked
              ? 'border-brand-500 bg-brand-500'
              : props.error
                ? 'border-red-300 bg-white'
                : 'border-stone-300 bg-white',
          )}
        >
          {props.checked && <CheckCircle2 className="h-3 w-3 text-white" strokeWidth={3} />}
          <input
            type="checkbox"
            checked={props.checked}
            onChange={(e) => props.onChange(e.target.checked)}
            required={props.required}
            className="sr-only"
          />
        </span>
        <span className="text-[12px] leading-relaxed text-stone-600">{props.children}</span>
      </label>
      {props.error && (
        <p className="mt-1 pl-8 text-[11px] font-medium text-red-600" role="alert">
          {props.error}
        </p>
      )}
    </div>
  );
}

